import {createListenerMiddleware, ListenerEffect, ThunkDispatch, UnknownAction} from "@reduxjs/toolkit";

import {userComUserRegister} from "@pg-mono/user-com";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {IRootState} from "../../store/store";
import {userSignInHit} from "../../tracking/algolytics_hits/user_sign_in_hit";
import {userSignUpHit} from "../../tracking/algolytics_hits/user_sign_up_hit";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {fetchUserUnsubscribeLink} from "../../user/api/fetch_user_unsubscribe_link";
import {getUserProfileApi} from "../../user/api/get_user_profile";
import {IUserProfile} from "../../user/types/IUserProfile";
import {authSucceeded, IAuthSucceededPayload, IPostAuthFlowContext} from "../actions/auth_succeeded_event";
import {REGISTRATION_FLOW_ABOVE_AI_ASSISTANT_Z_INDEX} from "../constants/registration_flow_modal_z_index";
import {closeFlow, IStartRegistrationFlowPayload, nextStep, startRegistrationFlow, startRegistrationFlowByVariant} from "../state/registration_flow_slice";
import {AuthActionType} from "../types/AuthActionType";
import {RegistrationFlowStep} from "../types/RegistrationFlowStep";
import {RegistrationWizardAccountEntry} from "../types/RegistrationWizardEntry";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {getRegistrationFlowUserState} from "../utils/get_registration_flow_user_state";
import {isRegistrationFlowHostMounted} from "../utils/registration_flow_host";

/**
 * Listener for handling post-authentication actions (login or registration)
 * This should be triggered after user logs in or registers via login/register form
 * without use of kontomieszkaniowe.pl
 *
 * Use cases:
 * - Two-step application forms [CU-2t405gt]
 * - Hidden prices [CU-862j5xdx7]
 * - Registration after application sent
 */
export const afterAuthListener = createListenerMiddleware<IRootState>();

const USER_PROFILE_WAIT_TIMEOUT_MS = 20000;
const GOOGLE_ONE_TAP_AUTH_PLACEMENT: AmplitudeAuthPlacement = "google_popup";
const GOOGLE_ONE_TAP_PRICE_CTA_LABEL = "Odkryj cenę";
type AfterAuthDispatch = ThunkDispatch<IRootState, unknown, UnknownAction>;

type RegistrationFlowSnapshot = Pick<
    IRootState["registrationFlow"],
    "isActive" | "currentStep" | "propertyId" | "offerId" | "vendorId" | "userSource" | "useLeadAfterRegistration"
>;

const getRegistrationFlowSnapshot = (registrationFlow: IRootState["registrationFlow"]): RegistrationFlowSnapshot => ({
    isActive: registrationFlow.isActive,
    currentStep: registrationFlow.currentStep,
    propertyId: registrationFlow.propertyId,
    offerId: registrationFlow.offerId,
    vendorId: registrationFlow.vendorId,
    userSource: registrationFlow.userSource,
    useLeadAfterRegistration: registrationFlow.useLeadAfterRegistration
});

const getExpectedActiveFlowSnapshot = (fallbackSnapshot: RegistrationFlowSnapshot, postAuthFlowContext?: IPostAuthFlowContext): RegistrationFlowSnapshot => {
    if (!postAuthFlowContext?.flowWasActive) {
        return fallbackSnapshot;
    }

    return {
        isActive: true,
        currentStep: postAuthFlowContext.currentStep ?? fallbackSnapshot.currentStep,
        propertyId: postAuthFlowContext.registrationFlow.propertyId,
        offerId: postAuthFlowContext.registrationFlow.offerId,
        vendorId: postAuthFlowContext.registrationFlow.vendorId,
        userSource: postAuthFlowContext.registrationFlow.userSource,
        useLeadAfterRegistration: postAuthFlowContext.registrationFlow.useLeadAfterRegistration
    };
};

const isSameActiveFlow = (registrationFlow: IRootState["registrationFlow"], snapshot: RegistrationFlowSnapshot) => {
    return (
        registrationFlow.isActive &&
        snapshot.isActive &&
        registrationFlow.currentStep === snapshot.currentStep &&
        registrationFlow.propertyId === snapshot.propertyId &&
        registrationFlow.offerId === snapshot.offerId &&
        registrationFlow.vendorId === snapshot.vendorId &&
        registrationFlow.userSource === snapshot.userSource &&
        registrationFlow.useLeadAfterRegistration === snapshot.useLeadAfterRegistration
    );
};

/**
 * Waits for the profile refresh triggered by `fetchUserDataAfterAuthListener`. Every problem is reported and
 * resolved as `null` instead of aborting the caller - authentication already succeeded, so the post-auth flow
 * has to be resolved either way
 */
const takeUserProfile = async (
    listenerApi: Parameters<ListenerEffect<ReturnType<typeof authSucceeded>, IRootState, AfterAuthDispatch>>[1],
    authPayload: IAuthSucceededPayload
): Promise<IUserProfile | null> => {
    let getUserProfileResult: Awaited<ReturnType<typeof listenerApi.take>>;

    try {
        getUserProfileResult = await listenerApi.take(
            (profileAction) =>
                /**
                 * Other consumers (mounting components, concurrent listeners) emit `getUserProfile` actions that
                 * carry no usable profile and must not end the wait: a deduplicated request is rejected with
                 * `meta.condition`, and the anonymous guard resolves with a null payload while the session cache
                 * is still being revalidated. Skip both and keep waiting for the actual refresh
                 */
                (getUserProfileApi.endpoints.getUserProfile.matchFulfilled(profileAction) && profileAction.payload != null) ||
                (getUserProfileApi.endpoints.getUserProfile.matchRejected(profileAction) && !profileAction.meta.condition),
            USER_PROFILE_WAIT_TIMEOUT_MS
        );
    } catch (error) {
        if (listenerApi.signal?.aborted) {
            return null;
        }

        notifyBugsnag(error, "Failed while waiting for user profile after authentication", {authPayload});
        return null;
    }

    if (!getUserProfileResult) {
        notifyBugsnag(new Error("Timed out while waiting for user profile"), "User profile timeout after authentication", {authPayload});
        return null;
    }

    const profileAction = getUserProfileResult[0];

    if (getUserProfileApi.endpoints.getUserProfile.matchRejected(profileAction)) {
        notifyBugsnag(profileAction.error, "Failed to fetch user profile after authentication", {authPayload});
        return null;
    }

    if (!getUserProfileApi.endpoints.getUserProfile.matchFulfilled(profileAction) || !profileAction.payload) {
        notifyBugsnag(new Error("getUserProfile does not return a valid data"), "Problem with getUserProfile", {authPayload});
        return null;
    }

    return profileAction.payload;
};

const getWizardModalZIndex = (state: IRootState): number | undefined =>
    state.semanticSearchModal.surfacePhase === "closed" ? undefined : REGISTRATION_FLOW_ABOVE_AI_ASSISTANT_Z_INDEX;

const startPostAuthFlow = (
    dispatch: AfterAuthDispatch,
    flows: {wizardFlow: IStartRegistrationFlowPayload; controlFlow: IStartRegistrationFlowPayload | null; isProfileKnown: boolean}
) => {
    const {wizardFlow, controlFlow, isProfileKnown} = flows;

    if (!isProfileKnown || !isRegistrationFlowHostMounted()) {
        if (controlFlow) {
            dispatch(startRegistrationFlow(controlFlow));
        }

        return;
    }

    dispatch(startRegistrationFlowByVariant(wizardFlow, controlFlow ? {type: "registration_flow", flow: controlFlow} : {type: "none"}));
};

interface IPostAuthFlowParams {
    state: IRootState;
    authPayload: IAuthSucceededPayload;
    userProfile: IUserProfile | null;
    userState: ReturnType<typeof getRegistrationFlowUserState>;
}

const advanceActiveFlow = (dispatch: AfterAuthDispatch, params: IPostAuthFlowParams, expectedActiveFlowSnapshot: RegistrationFlowSnapshot) => {
    const {state, authPayload, userProfile, userState} = params;
    const {hasPhoneVerified, hasCompleteProfile, hasPhone, hasName} = userState;

    if (expectedActiveFlowSnapshot.currentStep !== RegistrationFlowStep.REGISTRATION || !isSameActiveFlow(state.registrationFlow, expectedActiveFlowSnapshot)) {
        return;
    }

    if (!userProfile && authPayload.type !== AuthActionType.REGISTRATION) {
        /**
         * Login succeeded but the profile is unknown, so there is no way to tell which onboarding
         * step is due. Closing beats guessing - and beats leaving the user on a filled in login
         * form that never reacts
         */
        dispatch(closeFlow());
        return;
    }

    dispatch(nextStep({hasPhoneVerified, hasCompleteProfile, hasPhone, hasName}));
};

const getWizardUserState = (params: IPostAuthFlowParams) => ({
    hasPhone: params.userState.hasPhone,
    hasName: params.userState.hasName,
    modalZIndex: getWizardModalZIndex(params.state)
});

const startOneTapPropertyLeadFlow = (dispatch: AfterAuthDispatch, params: IPostAuthFlowParams, oneTapContext: IPostAuthFlowContext) => {
    const {authPayload, userProfile, userState} = params;
    const propertyLeadFlow: IStartRegistrationFlowPayload = {
        ...oneTapContext.registrationFlow,
        isUserLoggedIn: true,
        hasCompleteProfile: userState.hasCompleteProfile,
        hasPhoneVerified: userState.hasPhoneVerified,
        registrationMethod: authPayload.registrationMethod,
        socialMediaType: authPayload.socialMediaType ?? "google",
        userSource: oneTapContext.registrationFlow.userSource ?? authPayload.userSource,
        gtmSourceSection: oneTapContext.registrationFlow.gtmSourceSection ?? authPayload.gtmSourceSection
    };

    startPostAuthFlow(dispatch, {
        wizardFlow: {
            ...propertyLeadFlow,
            ...getWizardUserState(params),
            unlockChoiceEntryPoint: UnlockChoiceEntryPoint.PRICE,
            unlockChoiceOrigin: "cta",
            entryCtaLabel: GOOGLE_ONE_TAP_PRICE_CTA_LABEL,
            amplitudePlacement: GOOGLE_ONE_TAP_AUTH_PLACEMENT
        },
        controlFlow: propertyLeadFlow,
        isProfileKnown: userProfile !== null
    });
};

const startFlowAfterStandaloneAuth = (dispatch: AfterAuthDispatch, params: IPostAuthFlowParams) => {
    const {authPayload, userProfile, userState} = params;
    const isOneTap = authPayload.authOrigin === "google_one_tap";
    const isProfileKnown = userProfile !== null;
    const oneTapContext = isOneTap ? authPayload.postAuthFlowContext : undefined;

    if (oneTapContext && !oneTapContext.flowWasActive) {
        startOneTapPropertyLeadFlow(dispatch, params, oneTapContext);
        return;
    }

    const signUpFlow: IStartRegistrationFlowPayload = {
        hasCompleteProfile: userState.hasCompleteProfile,
        hasPhoneVerified: userState.hasPhoneVerified,
        isUserLoggedIn: true,
        registrationMethod: authPayload.registrationMethod,
        socialMediaType: authPayload.socialMediaType,
        userSource: authPayload.userSource,
        gtmSourceSection: authPayload.gtmSourceSection,
        modalVariant: "hidden_prices_variant_b"
    };
    const signUpWizardFlow: IStartRegistrationFlowPayload = {
        ...signUpFlow,
        ...getWizardUserState(params),
        wizardEntry: RegistrationWizardAccountEntry.SIGN_UP,
        amplitudePlacement: isOneTap ? GOOGLE_ONE_TAP_AUTH_PLACEMENT : null
    };

    if (authPayload.type === AuthActionType.REGISTRATION) {
        const validRegistrationFlowSources: UserSource[] = [UserSource.NAVIGATION, UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP];

        if (validRegistrationFlowSources.includes(authPayload.userSource)) {
            startPostAuthFlow(dispatch, {wizardFlow: signUpWizardFlow, controlFlow: signUpFlow, isProfileKnown});
        }
    } else if (isOneTap && isProfileKnown && !userState.hasPhoneVerified) {
        startPostAuthFlow(dispatch, {wizardFlow: signUpWizardFlow, controlFlow: null, isProfileKnown});
    }
};

const trackAfterAuth = (state: IRootState, authPayload: IAuthSucceededPayload, userProfile: IUserProfile) => {
    const afterRegister = authPayload.type === AuthActionType.REGISTRATION;

    try {
        const currentViewType = state.viewType.current;
        (afterRegister ? userSignUpHit : userSignInHit)(authPayload.userSource, userProfile, currentViewType);

        if (afterRegister) {
            const {email, uuid, email_marketing_consent, phone_marketing_consent} = userProfile;

            fetchUserUnsubscribeLink()
                .then((response) => {
                    if (!response) {
                        return;
                    }

                    const {unsubscribe_url_rp, unsubscribe_url_gh} = response;
                    userComUserRegister(uuid, email, email_marketing_consent, phone_marketing_consent, unsubscribe_url_rp, unsubscribe_url_gh);
                })
                .catch((error) => {
                    notifyBugsnag(error, "Failed to fetch unsubscribe link for user.com registration");
                });
        }
    } catch (error) {
        notifyBugsnag(error, "Failed to process after auth tracking", {
            authPayload
        });
    }
};

export const handleAfterAuth: ListenerEffect<ReturnType<typeof authSucceeded>, IRootState, AfterAuthDispatch> = async (action, listenerApi) => {
    const authPayload = action.payload;
    const flowSnapshotAtAuth = getRegistrationFlowSnapshot(listenerApi.getState().registrationFlow);
    const flowWasActiveAtAuth = Boolean(authPayload.postAuthFlowContext?.flowWasActive || flowSnapshotAtAuth.isActive);
    const expectedActiveFlowSnapshot = getExpectedActiveFlowSnapshot(flowSnapshotAtAuth, authPayload.postAuthFlowContext);

    listenerApi.cancelActiveListeners?.();

    const userProfile = await takeUserProfile(listenerApi, authPayload);

    if (listenerApi.signal?.aborted) {
        return;
    }

    const state = listenerApi.getState();
    const flowParams: IPostAuthFlowParams = {state, authPayload, userProfile, userState: getRegistrationFlowUserState(userProfile)};

    try {
        if (flowWasActiveAtAuth) {
            advanceActiveFlow(listenerApi.dispatch, flowParams, expectedActiveFlowSnapshot);
        } else if (!state.registrationFlow.isActive) {
            startFlowAfterStandaloneAuth(listenerApi.dispatch, flowParams);
        }
    } catch (error) {
        notifyBugsnag(error, "Failed to update registration flow after authentication", {authPayload});
    }

    if (userProfile) {
        trackAfterAuth(state, authPayload, userProfile);
    }
};

afterAuthListener.startListening({
    matcher: authSucceeded.match,
    effect: handleAfterAuth
});
import {createListenerMiddleware, ListenerEffect, ThunkDispatch, UnknownAction} from "@reduxjs/toolkit";

import {userComUserRegister} from "@pg-mono/user-com";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {IRootState} from "../../store/store";
import {userSignInHit} from "../../tracking/algolytics_hits/user_sign_in_hit";
import {userSignUpHit} from "../../tracking/algolytics_hits/user_sign_up_hit";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {fetchUserUnsubscribeLink} from "../../user/api/fetch_user_unsubscribe_link";
import {getUserProfileApi} from "../../user/api/get_user_profile";
import {IUserProfile} from "../../user/types/IUserProfile";
import {authSucceeded, IAuthSucceededPayload, IPostAuthFlowContext} from "../actions/auth_succeeded_event";
import {REGISTRATION_FLOW_ABOVE_AI_ASSISTANT_Z_INDEX} from "../constants/registration_flow_modal_z_index";
import {closeFlow, IStartRegistrationFlowPayload, nextStep, startRegistrationFlow, startRegistrationFlowByVariant} from "../state/registration_flow_slice";
import {AuthActionType} from "../types/AuthActionType";
import {RegistrationFlowStep} from "../types/RegistrationFlowStep";
import {RegistrationWizardAccountEntry} from "../types/RegistrationWizardEntry";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {getRegistrationFlowUserState} from "../utils/get_registration_flow_user_state";
import {isRegistrationFlowHostMounted} from "../utils/registration_flow_host";

/**
 * Listener for handling post-authentication actions (login or registration)
 * This should be triggered after user logs in or registers via login/register form
 * without use of kontomieszkaniowe.pl
 *
 * Use cases:
 * - Two-step application forms [CU-2t405gt]
 * - Hidden prices [CU-862j5xdx7]
 * - Registration after application sent
 */
export const afterAuthListener = createListenerMiddleware<IRootState>();

const USER_PROFILE_WAIT_TIMEOUT_MS = 20000;
const GOOGLE_ONE_TAP_AUTH_PLACEMENT: AmplitudeAuthPlacement = "google_popup";
const GOOGLE_ONE_TAP_PRICE_CTA_LABEL = "Odkryj cenę";
type AfterAuthDispatch = ThunkDispatch<IRootState, unknown, UnknownAction>;

type RegistrationFlowSnapshot = Pick<
    IRootState["registrationFlow"],
    "isActive" | "currentStep" | "propertyId" | "offerId" | "vendorId" | "userSource" | "useLeadAfterRegistration"
>;

const getRegistrationFlowSnapshot = (registrationFlow: IRootState["registrationFlow"]): RegistrationFlowSnapshot => ({
    isActive: registrationFlow.isActive,
    currentStep: registrationFlow.currentStep,
    propertyId: registrationFlow.propertyId,
    offerId: registrationFlow.offerId,
    vendorId: registrationFlow.vendorId,
    userSource: registrationFlow.userSource,
    useLeadAfterRegistration: registrationFlow.useLeadAfterRegistration
});

const getExpectedActiveFlowSnapshot = (fallbackSnapshot: RegistrationFlowSnapshot, postAuthFlowContext?: IPostAuthFlowContext): RegistrationFlowSnapshot => {
    if (!postAuthFlowContext?.flowWasActive) {
        return fallbackSnapshot;
    }

    return {
        isActive: true,
        currentStep: postAuthFlowContext.currentStep ?? fallbackSnapshot.currentStep,
        propertyId: postAuthFlowContext.registrationFlow.propertyId,
        offerId: postAuthFlowContext.registrationFlow.offerId,
        vendorId: postAuthFlowContext.registrationFlow.vendorId,
        userSource: postAuthFlowContext.registrationFlow.userSource,
        useLeadAfterRegistration: postAuthFlowContext.registrationFlow.useLeadAfterRegistration
    };
};

const isSameActiveFlow = (registrationFlow: IRootState["registrationFlow"], snapshot: RegistrationFlowSnapshot) => {
    return (
        registrationFlow.isActive &&
        snapshot.isActive &&
        registrationFlow.currentStep === snapshot.currentStep &&
        registrationFlow.propertyId === snapshot.propertyId &&
        registrationFlow.offerId === snapshot.offerId &&
        registrationFlow.vendorId === snapshot.vendorId &&
        registrationFlow.userSource === snapshot.userSource &&
        registrationFlow.useLeadAfterRegistration === snapshot.useLeadAfterRegistration
    );
};

/**
 * Waits for the profile refresh triggered by `fetchUserDataAfterAuthListener`. Every problem is reported and
 * resolved as `null` instead of aborting the caller - authentication already succeeded, so the post-auth flow
 * has to be resolved either way
 */
const takeUserProfile = async (
    listenerApi: Parameters<ListenerEffect<ReturnType<typeof authSucceeded>, IRootState, AfterAuthDispatch>>[1],
    authPayload: IAuthSucceededPayload
): Promise<IUserProfile | null> => {
    let getUserProfileResult: Awaited<ReturnType<typeof listenerApi.take>>;

    try {
        getUserProfileResult = await listenerApi.take(
            (profileAction) =>
                /**
                 * Other consumers (mounting components, concurrent listeners) emit `getUserProfile` actions that
                 * carry no usable profile and must not end the wait: a deduplicated request is rejected with
                 * `meta.condition`, and the anonymous guard resolves with a null payload while the session cache
                 * is still being revalidated. Skip both and keep waiting for the actual refresh
                 */
                (getUserProfileApi.endpoints.getUserProfile.matchFulfilled(profileAction) && profileAction.payload != null) ||
                (getUserProfileApi.endpoints.getUserProfile.matchRejected(profileAction) && !profileAction.meta.condition),
            USER_PROFILE_WAIT_TIMEOUT_MS
        );
    } catch (error) {
        if (listenerApi.signal?.aborted) {
            return null;
        }

        notifyBugsnag(error, "Failed while waiting for user profile after authentication", {authPayload});
        return null;
    }

    if (!getUserProfileResult) {
        notifyBugsnag(new Error("Timed out while waiting for user profile"), "User profile timeout after authentication", {authPayload});
        return null;
    }

    const profileAction = getUserProfileResult[0];

    if (getUserProfileApi.endpoints.getUserProfile.matchRejected(profileAction)) {
        notifyBugsnag(profileAction.error, "Failed to fetch user profile after authentication", {authPayload});
        return null;
    }

    if (!getUserProfileApi.endpoints.getUserProfile.matchFulfilled(profileAction) || !profileAction.payload) {
        notifyBugsnag(new Error("getUserProfile does not return a valid data"), "Problem with getUserProfile", {authPayload});
        return null;
    }

    return profileAction.payload;
};

const getWizardModalZIndex = (state: IRootState): number | undefined =>
    state.semanticSearchModal.surfacePhase === "closed" ? undefined : REGISTRATION_FLOW_ABOVE_AI_ASSISTANT_Z_INDEX;

const startPostAuthFlow = (
    dispatch: AfterAuthDispatch,
    flows: {wizardFlow: IStartRegistrationFlowPayload; controlFlow: IStartRegistrationFlowPayload | null; isProfileKnown: boolean}
) => {
    const {wizardFlow, controlFlow, isProfileKnown} = flows;

    if (!isProfileKnown || !isRegistrationFlowHostMounted()) {
        if (controlFlow) {
            dispatch(startRegistrationFlow(controlFlow));
        }

        return;
    }

    dispatch(startRegistrationFlowByVariant(wizardFlow, controlFlow ? {type: "registration_flow", flow: controlFlow} : {type: "none"}));
};

interface IPostAuthFlowParams {
    state: IRootState;
    authPayload: IAuthSucceededPayload;
    userProfile: IUserProfile | null;
    userState: ReturnType<typeof getRegistrationFlowUserState>;
}

const advanceActiveFlow = (dispatch: AfterAuthDispatch, params: IPostAuthFlowParams, expectedActiveFlowSnapshot: RegistrationFlowSnapshot) => {
    const {state, authPayload, userProfile, userState} = params;
    const {hasPhoneVerified, hasCompleteProfile, hasPhone, hasName} = userState;

    if (expectedActiveFlowSnapshot.currentStep !== RegistrationFlowStep.REGISTRATION || !isSameActiveFlow(state.registrationFlow, expectedActiveFlowSnapshot)) {
        return;
    }

    if (!userProfile && authPayload.type !== AuthActionType.REGISTRATION) {
        /**
         * Login succeeded but the profile is unknown, so there is no way to tell which onboarding
         * step is due. Closing beats guessing - and beats leaving the user on a filled in login
         * form that never reacts
         */
        dispatch(closeFlow());
        return;
    }

    dispatch(nextStep({hasPhoneVerified, hasCompleteProfile, hasPhone, hasName}));
};

const getWizardUserState = (params: IPostAuthFlowParams) => ({
    hasPhone: params.userState.hasPhone,
    hasName: params.userState.hasName,
    modalZIndex: getWizardModalZIndex(params.state)
});

const startOneTapPropertyLeadFlow = (dispatch: AfterAuthDispatch, params: IPostAuthFlowParams, oneTapContext: IPostAuthFlowContext) => {
    const {authPayload, userProfile, userState} = params;
    const propertyLeadFlow: IStartRegistrationFlowPayload = {
        ...oneTapContext.registrationFlow,
        isUserLoggedIn: true,
        hasCompleteProfile: userState.hasCompleteProfile,
        hasPhoneVerified: userState.hasPhoneVerified,
        registrationMethod: authPayload.registrationMethod,
        socialMediaType: authPayload.socialMediaType ?? "google",
        userSource: oneTapContext.registrationFlow.userSource ?? authPayload.userSource,
        gtmSourceSection: oneTapContext.registrationFlow.gtmSourceSection ?? authPayload.gtmSourceSection
    };

    startPostAuthFlow(dispatch, {
        wizardFlow: {
            ...propertyLeadFlow,
            ...getWizardUserState(params),
            unlockChoiceEntryPoint: UnlockChoiceEntryPoint.PRICE,
            unlockChoiceOrigin: "cta",
            entryCtaLabel: GOOGLE_ONE_TAP_PRICE_CTA_LABEL,
            amplitudePlacement: GOOGLE_ONE_TAP_AUTH_PLACEMENT
        },
        controlFlow: propertyLeadFlow,
        isProfileKnown: userProfile !== null
    });
};

const startFlowAfterStandaloneAuth = (dispatch: AfterAuthDispatch, params: IPostAuthFlowParams) => {
    const {authPayload, userProfile, userState} = params;
    const isOneTap = authPayload.authOrigin === "google_one_tap";
    const isProfileKnown = userProfile !== null;
    const oneTapContext = isOneTap ? authPayload.postAuthFlowContext : undefined;

    if (oneTapContext && !oneTapContext.flowWasActive) {
        startOneTapPropertyLeadFlow(dispatch, params, oneTapContext);
        return;
    }

    const signUpFlow: IStartRegistrationFlowPayload = {
        hasCompleteProfile: userState.hasCompleteProfile,
        hasPhoneVerified: userState.hasPhoneVerified,
        isUserLoggedIn: true,
        registrationMethod: authPayload.registrationMethod,
        socialMediaType: authPayload.socialMediaType,
        userSource: authPayload.userSource,
        gtmSourceSection: authPayload.gtmSourceSection,
        modalVariant: "hidden_prices_variant_b"
    };
    const signUpWizardFlow: IStartRegistrationFlowPayload = {
        ...signUpFlow,
        ...getWizardUserState(params),
        wizardEntry: RegistrationWizardAccountEntry.SIGN_UP,
        amplitudePlacement: isOneTap ? GOOGLE_ONE_TAP_AUTH_PLACEMENT : null
    };

    if (authPayload.type === AuthActionType.REGISTRATION) {
        const validRegistrationFlowSources: UserSource[] = [UserSource.NAVIGATION, UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP];

        if (validRegistrationFlowSources.includes(authPayload.userSource)) {
            startPostAuthFlow(dispatch, {wizardFlow: signUpWizardFlow, controlFlow: signUpFlow, isProfileKnown});
        }
    } else if (isOneTap && isProfileKnown && !userState.hasPhoneVerified) {
        startPostAuthFlow(dispatch, {wizardFlow: signUpWizardFlow, controlFlow: null, isProfileKnown});
    }
};

const trackAfterAuth = (state: IRootState, authPayload: IAuthSucceededPayload, userProfile: IUserProfile) => {
    const afterRegister = authPayload.type === AuthActionType.REGISTRATION;

    try {
        const currentViewType = state.viewType.current;
        (afterRegister ? userSignUpHit : userSignInHit)(authPayload.userSource, userProfile, currentViewType);

        if (afterRegister) {
            const {email, uuid, email_marketing_consent, phone_marketing_consent} = userProfile;

            fetchUserUnsubscribeLink()
                .then((response) => {
                    if (!response) {
                        return;
                    }

                    const {unsubscribe_url_rp, unsubscribe_url_gh} = response;
                    userComUserRegister(uuid, email, email_marketing_consent, phone_marketing_consent, unsubscribe_url_rp, unsubscribe_url_gh);
                })
                .catch((error) => {
                    notifyBugsnag(error, "Failed to fetch unsubscribe link for user.com registration");
                });
        }
    } catch (error) {
        notifyBugsnag(error, "Failed to process after auth tracking", {
            authPayload
        });
    }
};

export const handleAfterAuth: ListenerEffect<ReturnType<typeof authSucceeded>, IRootState, AfterAuthDispatch> = async (action, listenerApi) => {
    const authPayload = action.payload;
    const flowSnapshotAtAuth = getRegistrationFlowSnapshot(listenerApi.getState().registrationFlow);
    const flowWasActiveAtAuth = Boolean(authPayload.postAuthFlowContext?.flowWasActive || flowSnapshotAtAuth.isActive);
    const expectedActiveFlowSnapshot = getExpectedActiveFlowSnapshot(flowSnapshotAtAuth, authPayload.postAuthFlowContext);

    listenerApi.cancelActiveListeners?.();

    const userProfile = await takeUserProfile(listenerApi, authPayload);

    if (listenerApi.signal?.aborted) {
        return;
    }

    const state = listenerApi.getState();
    const flowParams: IPostAuthFlowParams = {state, authPayload, userProfile, userState: getRegistrationFlowUserState(userProfile)};

    try {
        if (flowWasActiveAtAuth) {
            advanceActiveFlow(listenerApi.dispatch, flowParams, expectedActiveFlowSnapshot);
        } else if (!state.registrationFlow.isActive) {
            startFlowAfterStandaloneAuth(listenerApi.dispatch, flowParams);
        }
    } catch (error) {
        notifyBugsnag(error, "Failed to update registration flow after authentication", {authPayload});
    }

    if (userProfile) {
        trackAfterAuth(state, authPayload, userProfile);
    }
};

afterAuthListener.startListening({
    matcher: authSucceeded.match,
    effect: handleAfterAuth
});
